import { useCallback, useEffect, useMemo, useState } from "react";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { Accordion, Badge, Button as BsButton } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { toast } from "react-toastify";
import { createApiService } from "./shared/apiConfig";
import { SubscriptionApi, SubscriptionGroupsApi } from "./shared/api/axios-client";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { ChevronDown, ChevronUp, Folder2Open, FolderPlus, Funnel, Grid3x3Gap, InfoCircle, Pencil, Trash } from "react-bootstrap-icons";
import { CostProjectionDto, CostProjectionSection } from "./CostProjectionSection";
import { mapCostProjection } from "./CostProjectionMapper";
import {
  InviteParkerForm,
  InviteParkerModal,
} from "./UserPlanDetailsPageInviteParkerModal";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { SubscribeUpdateReq, Vehicle } from "./Data/ApiTypes";
import { ChangeParkerGroupModal } from "./ChangeParkerGroupModal";
import ParkerEditModal from "./ParkerEditModal";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { ChooseVehicleModal } from "./Controls/ChooseVehicleModal";
import { showMoney } from "./Data/Money";
import { useSubscriptionParkersActions } from "./UseSubscriptionParkersActions";
import {
  usePendingChanges,
  useUnsavedChangesGuard,
} from "./PendingChangesContext";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { parseParkerInfo } from "./Data/ApiParse";
import { UnsavedChangesModal } from "./UnsavedChangesModal";
import { PageTitle } from "./Controls/PageTitle";
import { GroupModal } from "./GroupModal";
import { ActionDropdown } from "./ActionDropdown";
import ContractDetailsModal from "./ContractDetailsModal";
import {
  GroupParkersHelpDialog,
  isGroupParkersHelpDismissed,
} from "./Controls/GroupParkersHelpDialog";
import { CommitChangesConfirmDialog } from "./Controls/CommitChangesConfirmDialog";
import "./SubscriptionManageGroupParkersPage.scss";
import { formatIsoDateShort, localDateToInstantUtc } from "./Data/Date";

enum Context {
  FromGroup,
  FromPlanGroups,
}

export const UNASSIGNED_GROUP_ID = 0;

export function SubscriptionManageGroupParkersPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  const [subscription, loadedGroups] = useLoaderData() as any;

  const groupId = searchParams.get("groupId")
    ? Number(searchParams.get("groupId"))
    : null;

  const context: Context = groupId ? Context.FromGroup : Context.FromPlanGroups;

  const [committedParkers, setCommittedParkers] = useState<any[]>([]);
  const [committedSpaces, setCommittedSpaces] = useState<number>(
    subscription.quantity
  );
  const [editedSpaces, setEditedSpaces] = useState<number>(
    subscription.quantity
  );

  const [allGuestParkers, setAllGuestParkers] = useState<any[]>([]);

  const [groupGuestParkers, setGroupGuestParkers] = useState<any[]>([]);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(0);

  const [search, setSearch] = useState("");
  const [showAddParker, setShowAddParker] = useState(false);

  const [showRemove, setShowRemove] = useState(false);
  const [removeParkerId, setRemoveParkerId] = useState<number | null>(null);
  const [removeParkerFromGroup, setRemoveParkerFromGroup] = useState(false);
  const [showChangeGroup, setShowChangeGroup] = useState(false);
  const [selectedParker, setSelectedParker] = useState<any | null>(null);
  const [showEditParkerModal, setShowEditParkerModal] = useState(false);
  const [chooseModalVehicles, setChooseModalVehicles] = useState<Vehicle[]>([]);
  const [chooseModalExclude, setChooseModalExclude] = useState<number[]>([]);
  const [showChooseVehicle, setShowChooseVehicle] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractModalGroupId, setContractModalGroupId] = useState<number | null>(null);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showCommitConfirmDialog, setShowCommitConfirmDialog] = useState(false);
  const [groups, setGroups] = useState(loadedGroups);

  // Cost projection state
  const [currentProjection, setCurrentProjection] = useState<CostProjectionDto | null>(null);
  const [nextProjection, setNextProjection] = useState<CostProjectionDto | null>(null);
  const [costProjectionLoading, setCostProjectionLoading] = useState(false);
  const [hasFetchedCostProjection, setHasFetchedCostProjection] = useState(false);

  // Collapsible sections state
  const [spaceAllocationExpanded, setSpaceAllocationExpanded] = useState(true);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const { hasChanges, setHasChanges, setHandlers } = usePendingChanges();
  const { showModal, stay, discard } = useUnsavedChangesGuard(
    hasChanges,
    () => {
      setAllGuestParkers(committedParkers);
      setHasChanges(false);
    }
  );
  const subGroupsApi = createApiService(SubscriptionGroupsApi);

  const {
    parkerRemove,
    vehicleAdd,
    vehicleRemove,
    vehicleLink,
    parkerInvite,
    openChooseModalForParkersList,
  } = useSubscriptionParkersActions({
    subscriptionId: subscription.subscriptionId,
    setParkers: setAllGuestParkers,
    selectedParker,
    setSelectedParker,
    setShowChooseVehicle: setShowChooseVehicle,
    setChooseModalVehicles: setChooseModalVehicles,
    setChooseModalExclude: setChooseModalExclude,
    groups,
  });

  useEffect(() => {
    if (!subscription?.parkers) return;

    const guests = subscription.parkers.map((guest: any) => ({
      subscriptionId: subscription.subscriptionId,
      sub: subscription,
      parkerId: guest.parker.parkerId,
      name: guest.parker.name,
      email: guest.parker.emailAddress,
      plan: subscription.plan.name,
      planId: subscription.plan.planId,
      isManaged: guest.parker.isManaged,
      quantity: guest.quantity,
      parker: guest.parker,
      vehicles: guest.vehicles,
      facilityId: subscription.plan.facilityId,
      groupId: guest.subscriptionGroupId,
      groupName: guest.subscriptionGroupName,
      startDate: guest.startDate,
    }));

    setCommittedParkers(guests);
    setAllGuestParkers(guests);

    setCommittedSpaces(subscription.quantity);
    setEditedSpaces(subscription.quantity);
  }, [subscription]);

  // Show help dialog on load when no parkers (or fromEmail in URL) and user hasn't dismissed it
  useEffect(() => {
    if (!subscription?.parkers) return;
    const hasNoParkers = subscription.parkers.length === 0;
    const hasFromEmail = searchParams.get("fromEmail") !== null;
    const shouldShow =
      (hasFromEmail || hasNoParkers) && !isGroupParkersHelpDismissed();
    if (shouldShow) setShowHelpDialog(true);
  }, [subscription?.parkers, searchParams]);

  useEffect(() => {
    if (
      context === Context.FromPlanGroups &&
      groups.length > 0 &&
      selectedGroupId === 0
    ) {
      setSelectedGroupId(0);
    } else if (context === Context.FromGroup) {
      setSelectedGroupId(groupId!);
    }
  }, [groups, context, selectedGroupId]);

  useEffect(() => {
    let guests = [...allGuestParkers];

    if (context === Context.FromGroup && selectedGroupId) {
      guests = guests.filter((p) => p.isDraft || p.groupId === selectedGroupId);
    }

    if (context === Context.FromPlanGroups && selectedGroupId !== 0) {
      guests = guests.filter((p) => p.isDraft || p.groupId === selectedGroupId);
    }

    if (search) {
      const searchTerm = search.toLowerCase();
      guests = guests.filter((p) => {
        const lpnText = getLpnText(p.vehicles).toLowerCase();
        return `${p.name} ${lpnText}`.toLowerCase().includes(searchTerm);
      });
    }

    setGroupGuestParkers(guests);
  }, [allGuestParkers, selectedGroupId, search, context]);

  const hasRealChanges = useMemo(() => {
    if (editedSpaces !== committedSpaces) return true;

    if (allGuestParkers.some((p) => p.isDraft)) return true;

    return allGuestParkers.some((p) => {
      const committed = committedParkers.find((c) => c.parkerId === p.parkerId);
      return committed && committed.groupId !== p.groupId;
    });
  }, [editedSpaces, committedSpaces, allGuestParkers, committedParkers]);

  useEffect(() => {
    setHasChanges(hasRealChanges);
  }, [hasRealChanges, setHasChanges]);

  const refreshCostProjection = async () => {
    setCostProjectionLoading(true);
    setHasFetchedCostProjection(true);
    try {
      const subscriptionApi = createApiService(SubscriptionApi);
      const response = (await subscriptionApi.subscriptionIdCostProjectionGet({ subscriptionId: subscription.subscriptionId })).data!;

      if (response.currentPeriod) {
        const mappedCurrent = mapCostProjection(
          response.currentPeriod,
          allGuestParkers,
        );
        setCurrentProjection(mappedCurrent);
      }

      if (response.nextPeriod) {
        const mappedNext = mapCostProjection(
          response.nextPeriod,
          allGuestParkers,
        );
        setNextProjection(mappedNext);
      }
    } finally {
      setCostProjectionLoading(false);
    }
  };

  // Auto-refresh cost projection if user has explicitly fetched data in this session
  useEffect(() => {
    if (!subscription.isEnhancedGroup) return;
    if (hasFetchedCostProjection) {
      refreshCostProjection();
    }
  }, [groups, allGuestParkers]);

  type ParkerRow = {
    parkerId: number;
    draftId?: string;
    isDraft: boolean;

    name: string;
    email: string;
    quantity: number;
    isManaged: boolean;

    parker: any;
    vehicles: Vehicle[];

    groupId: number | null;
    groupName: string | null;

    subscriptionId: number;
    lpn: string;
    plan: string;

    sub: {
      subscriptionId: number;
      status: SubscriptionStatus;
    };

    startDate?: string | null;
  };

  const saveChanges = useCallback(async () => {
    const updateReq: SubscribeUpdateReq = {
      facilityId: subscription!.facility.facilityId,
      subscriptionId: subscription.subscriptionId,
      newPlanEntryId: subscription?.planEntryId!,
      newQuantity: editedSpaces,
    };

    if (editedSpaces !== committedSpaces) {
      await Api.subscriptionUpdateDefer(updateReq).then((res) =>
        processResult(
          res,
          () => {
            setCommittedSpaces(editedSpaces);
          },
          (err) => toast.error(err)
        )
      );
    }

    const api = createApiService(SubscriptionGroupsApi);

    const groupChanges = allGuestParkers.filter(
      (p) =>
        !p.isDraft &&
        committedParkers.find((c) => c.parkerId === p.parkerId)?.groupId !==
          p.groupId
    );

    for (const p of groupChanges) {
      await api.changeParkerGroup({
        serverRequestsParkerGroupChangeRequest: {
          parkerId: p.parkerId!,
          subscriptionGroupId: p.groupId,
          subscriptionId: subscription.subscriptionId,
        },
      });
    }

    const drafts = allGuestParkers.filter((p) => p.isDraft);
    const created: ParkerRow[] = [];

    for (const d of drafts) {
      const resp = await Api.subParkerInvite(subscription.subscriptionId, {
        name: d.name,
        emailAddress: d.email,
        subscriptionGroupId: d.groupId,
        quantity: 1,
        isLoginEnabled: false,
        subscriptionId: subscription.subscriptionId,
        startDate: localDateToInstantUtc(d.startDate)
      });

      processResult(
        resp,
        (val) => {
          created.push({
            parkerId: val.parker.parkerId,
            isDraft: false,

            name: val.parker.name,
            email: val.parker.emailAddress,
            quantity: val.quantity,
            isManaged: val.parker.isManaged,

            parker: parseParkerInfo(val.parker),
            vehicles: [],

            groupId: d.groupId,
            groupName:
              d.groupId != null
                ? groups!.find((g) => g.id === d.groupId)?.name ?? null
                : null,

            subscriptionId: 0,
            lpn: "-",
            plan: "",

            sub: {
              subscriptionId: 0,
              status: SubscriptionStatus.Active,
            },

            startDate: val.startDate ?? d.startDate ?? undefined,
          });
        },
        (err) => {
            toast.error(err);
        }
      );
    }

    const spacesChanged = editedSpaces !== committedSpaces;
    const parkersAdded = drafts.length > 0;
    const parkersRemoved = committedParkers.some(
      (c) => !allGuestParkers.find((p) => p.parkerId === c.parkerId && !p.isDraft)
    );
    const parkersChangedGroups = groupChanges.length > 0;

    if (spacesChanged || parkersAdded || parkersRemoved || parkersChangedGroups) {
      await Api.subscriptionUpdateEnhanced(updateReq).then((enhancedRes) =>
        processResult(
          enhancedRes,
          () => {},
          (err) => toast.error(err)
        )
      );
    }

    const committed = [
      ...allGuestParkers.filter((p) => !p.isDraft),
      ...created,
    ];

    setAllGuestParkers(committed);
    setCommittedParkers(committed);
    setHasChanges(false);

    toast.success("Changes saved successfully");
  }, [
    editedSpaces,
    committedSpaces,
    allGuestParkers,
    committedParkers,
    subscription.subscriptionId,
    setHasChanges,
  ]);

  const cancelChanges = useCallback(() => {
    setAllGuestParkers([...committedParkers]);
    setEditedSpaces(committedSpaces);
    setSelectedParker(null);
    setShowCancelConfirm(false);
    setHasChanges(false);
  }, [committedParkers, committedSpaces]);

  useEffect(() => {
    setHandlers({
      onSave: () => setShowCommitConfirmDialog(true),
      onDiscard: () => setShowCancelConfirm(true),
    });
  }, [setHandlers, cancelChanges]);

  const getLpnText = (vehicles: Vehicle[]) =>
    vehicles?.length
      ? vehicles.map((v) => v.licensePlateNumber).join(" | ")
      : "-";

  const getPendingRowStyle = (isDraft: boolean, defaultColor?: string) => ({
    backgroundColor: isDraft ? "rgb(255 248 248)" : defaultColor,
  });

  async function assignParkerToGroup(
    parker: { parkerId?: number; draftId?: string; isDraft: boolean },
    groupId: number | null
  ) {
    setAllGuestParkers((prev) =>
      prev.map((p) => {
        const isSame =
          p.isDraft && parker.isDraft
            ? p.draftId === parker.draftId
            : !p.isDraft && !parker.isDraft
            ? p.parkerId === parker.parkerId
            : false;

        if (!isSame) return p;

        return {
          ...p,
          groupId,
          groupName:
            groupId == null
              ? null
              : groups.find((g) => g.id === groupId)?.name ?? null,
        };
      })
    );
  }

  async function createGroup(name: string) {
    const res = await subGroupsApi.addGroup({
      serverRequestsSubscriptionGroupAddRequest: {
        subscriptionId: subscription.subscriptionId,
        name,
      },
    });

    const group = res.data;

    if (group?.error) {
      toast.error(group.error);
      return;
    }

    setGroups((prev) => [
      ...prev,
      {
        id: group.id,
        name: group.name,
        parkerCount: 0,
      },
    ]);

    toast.success("Group created successfully.");

    return group;
  }

  const totalSpaces = editedSpaces;
  const totalParkers = allGuestParkers.length;

  const unallocatedSpaces = totalSpaces - totalParkers;

  const hasAvailableSpaces = unallocatedSpaces > 0;

  const unassignedCount = useMemo(
    () => allGuestParkers.filter((p) => !p.groupId).length,
    [allGuestParkers]
  );

  const pendingCount = useMemo(
    () => allGuestParkers.filter((p) => p.isDraft).length,
    [allGuestParkers]
  );

  const hasUnassigned =
    context === Context.FromPlanGroups && unassignedCount > 0;

  const showWarning = hasUnassigned;

  const currentGroupName =
    context === Context.FromGroup && groupId
      ? groups.find((g) => g.id === groupId)?.name
      : null;

  const contractRules =
    context === Context.FromGroup && groupId
      ? groups.find((g) => g.id === groupId)?.contractRules
      : null;

  const groupIdForContract =
    groupId ?? (selectedGroupId && selectedGroupId > 0 ? selectedGroupId : null);
  const currentGroupHasContract =
    groupIdForContract != null &&
    (groups.find((g) => g.id === groupIdForContract)?.contractRules?.length ?? 0) > 0;
  const contractModalGroup =
    contractModalGroupId != null
      ? groups.find((g) => g.id === contractModalGroupId)
      : null;

  const openContractModal = (id: number) => {
    setContractModalGroupId(id);
    setShowContractModal(true);
  };

  const closeContractModal = () => {
    setShowContractModal(false);
    setContractModalGroupId(null);
  };

  const groupHasContract = (gId: number | null | undefined) =>
    gId != null && (groups.find((g) => g.id === gId)?.contractRules?.length ?? 0) > 0;

  return (
    <div className="container-fluid py-3 d-flex flex-column h-100">
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <BsButton
              variant="link"
              className="p-0 mb-2 text-decoration-none text-danger"
              onClick={() => nav(`/user/plan/${subscription.subscriptionId}`)}
            >
              ← Back to Plan
            </BsButton>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <PageTitle className="mb-0">Manage Plan</PageTitle>
              <button
                type="button"
                className="btn btn-link p-0 text-primary text-decoration-none d-flex align-items-center gap-1 small"
                onClick={() => setShowHelpDialog(true)}
                aria-label="Managing group parkers"
              >
                <InfoCircle size={18} />
                
              </button>
            </div>

            <div className="d-flex gap-2 fs-5 fw-semibold text-muted align-items-center">
              {currentGroupName && (
                <>
                  <Badge bg="secondary">{currentGroupName}</Badge>
                </>
              )}

              <span>{subscription.plan.name}</span>
            </div>
          </div>
        </div>
      </div>

      {!groupId && (
        <Accordion
          className="mb-3 cost-projection-accordion"
          activeKey={spaceAllocationExpanded ? "0" : undefined}
          onSelect={(eventKey) => setSpaceAllocationExpanded(eventKey === "0")}
        >
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <div className="d-flex align-items-center gap-2 w-100 me-3">
                {spaceAllocationExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                <Grid3x3Gap size={16} className="text-muted" />
                <span>{spaceAllocationExpanded ? "Hide" : "Show"} Space Allocation</span>
              </div>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="card border-0">
                <div className="card-body py-2">
                  <div className="row text-center align-items-stretch space-allocation-row">
                    <div className="col d-flex flex-column align-items-center">
                      <div className="text-muted small mb-2">TOTAL SPACES</div>

                      <input
                        type="number"
                        min={totalParkers}
                        value={editedSpaces}
                        onChange={(e) => setEditedSpaces(Number(e.target.value))}
                        className={"space-input"}
                      />

                      <div className="small text-muted mt-2">Editable</div>
                    </div>

                    <div className="col-auto px-0">
                      <div className="vertical-divider" />
                    </div>

                    <div className="col d-flex flex-column align-items-center">
                      <div className="text-muted small mb-2">TOTAL PARKERS</div>
                      <div className="fw-bold fs-3">{totalParkers}</div>
                    </div>

                    <div className="col-auto px-0">
                      <div className="vertical-divider" />
                    </div>

                    <div className="col d-flex flex-column align-items-center">
                      <div className="text-muted small mb-2">UNALLOCATED SPACES</div>

                      <div className="fw-bold fs-3 text-unallocated">
                        {unallocatedSpaces}
                      </div>

                      {unallocatedSpaces > 0 && (
                        <div className="small text-unallocated mt-1">
                          Base rate applies
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}

      {subscription.isEnhancedGroup && (
        <CostProjectionSection
          current={currentProjection}
          next={nextProjection}
          isAccountsReceivable={subscription.isAccountsReceivable}
          onLoad={refreshCostProjection}
          loading={costProjectionLoading}
          subscriptionStartDate={subscription.start?.toString?.() ?? undefined}
        />
      )}

      {showWarning && (
        <div className="alert alert-warning d-flex align-items-start gap-3 py-3 mb-3">
          <div className="fs-5 lh-1">⚠️</div>

          <div>
            <div className="fw-semibold mb-1">Attention Required</div>

            <div className="small mb-2">
              The following will be charged the base rate of{" "}
              <strong>{showMoney(subscription.price)}</strong>:
            </div>

            <ul className="mb-0 ps-3 small">
              {hasUnassigned && (
                <li>
                  <strong>{unassignedCount}</strong> parker
                  {unassignedCount > 1 ? "s are" : " is"} not assigned to a
                  group
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      <Accordion
        className="mb-3 cost-projection-accordion"
        activeKey={filtersExpanded ? "0" : undefined}
        onSelect={(eventKey) => setFiltersExpanded(eventKey === "0")}
      >
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <div className="d-flex align-items-center gap-2 w-100 me-3">
              {filtersExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              <Funnel size={16} className="text-muted" />
              <span>{filtersExpanded ? "Hide" : "Show"} Filters</span>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <div className="row g-4 align-items-end">
              {context === Context.FromPlanGroups && (
                <div className="col-md-4">
                  <label className="form-label small text-muted">
                    Filter by Group
                  </label>
                  <select
                    className="form-select"
                    value={selectedGroupId!}
                    onChange={(e) => {
                      const raw = Number(e.target.value);

                      if (raw === -1) {
                        setSelectedGroupId(null);
                      } else {
                        setSelectedGroupId(raw);
                      }
                    }}
                  >
                    <option value={0}>All Groups</option>
                    <option value={-1}>Unassigned</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-md-12">
                <input
                  className="form-control"
                  placeholder="Search by name or LPN..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <div
        className="card border-0 shadow-none flex-grow-1 overflow-auto "
        style={{
          paddingBottom: hasChanges ? 60 : 0,
        }}
      >
        <div className="card-header border-0 bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {groupGuestParkers.length} Parker
              {groupGuestParkers.length !== 1 ? "s" : ""}
            </h5>

            <div className="d-flex gap-2 align-items-center">
              {currentGroupHasContract && groupIdForContract != null && (
                <Button
                  size="sm"
                  className="btn-main"
                  onClick={() => openContractModal(groupIdForContract)}
                >
                  View Contract
                </Button>
              )}

              <Button
                size="sm"
                className={`btn-main ${
                  !hasAvailableSpaces ? "opacity-50" : ""
                }`}
                onClick={() => {
                  if (!hasAvailableSpaces) {
                    toast.error(
                      "No more spaces left, you need more to add a new parker"
                    );
                    return;
                  }
                  setShowAddParker(true);
                }}
              >
                Add Parker
              </Button>

              {subscription.status === SubscriptionStatus.Active &&
                !groupId && (
                  <Button
                    size="sm"
                    className="btn-main "
                    onClick={() => setShowGroupModal(true)}
                  >
                    Add Group
                  </Button>
                )}
            </div>
          </div>

          {pendingCount > 0 && (
            <div style={{ marginTop: "-6px" }} className="small text-muted">
              * {pendingCount} pending save
            </div>
          )}
        </div>

        <div className="card d-none d-md-block">
          <div className="table-responsive">
            <table className="table mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Group</th>
                  <th>LPN</th>
                  <th>Start Date</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupGuestParkers.map((p) => (
                  <tr key={p.parkerId || p.draftId}>
                    <td
                      className="fw-semibold text-primary py-3"
                      style={getPendingRowStyle(p.isDraft)}
                    >
                      {p.name}
                    </td>
                    <td className="py-3" style={getPendingRowStyle(p.isDraft)}>
                      {p.groupName ? (
                        groupHasContract(p.groupId) ? (
                          <button
                            type="button"
                            className="badge rounded-pill bg-danger-subtle text-danger px-3 py-2 border-0 text-decoration-none"
                            style={{ cursor: "pointer" }}
                            onClick={() => openContractModal(p.groupId!)}
                            title="View contract"
                          >
                            {p.groupName}
                          </button>
                        ) : (
                          <span className="badge rounded-pill bg-danger-subtle text-danger px-3 py-2">
                            {p.groupName}
                          </span>
                        )
                      ) : (
                        <span className="badge rounded-pill bg-light text-muted px-3 py-2">
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td style={getPendingRowStyle(p.isDraft)}>
                      {getLpnText(p.vehicles)}
                    </td>
                    <td className="py-3" style={getPendingRowStyle(p.isDraft)}>
                      {formatIsoDateShort(p.startDate)}
                    </td>

                    <td
                      className="text-end py-3 pe-4"
                      style={getPendingRowStyle(p.isDraft)}
                    >
                      <div className="d-inline-flex gap-2">
                        <ActionDropdown
                          actions={[
                            {
                              label: p.groupId
                                ? "Change Group"
                                : "Assign to Group",
                              icon: p.groupId ? (
                                <Folder2Open
                                  size={16}
                                  className="text-muted mb-1"
                                />
                              ) : (
                                <FolderPlus
                                  size={16}
                                  className="text-muted mb-1"
                                />
                              ),
                              onClick: () => {
                                setSelectedParker(p);
                                setShowChangeGroup(true);
                              },
                            },
                            ...(!p.isDraft
                              ? [
                                  {
                                    label: "Edit Parker",
                                    icon: (
                                      <Pencil
                                        size={16}
                                        className="text-muted cursor-pointer mb-1"
                                      />
                                    ),
                                    onClick: () => {
                                      setSelectedParker(p);
                                      setShowEditParkerModal(true);
                                    },
                                  },
                                ]
                              : []),
                            {
                              label: p.groupId
                                ? "Remove from Group"
                                : "Remove Parker",
                              icon: (
                                <Trash size={16} className="text-muted mb-1" />
                              ),
                              onClick: () => {
                                if (p.isDraft && p.draftId) {
                                  if (p.groupId) {
                                    setAllGuestParkers((prev) =>
                                      prev.map((x) =>
                                        x.draftId === p.draftId
                                          ? {
                                              ...x,
                                              groupId: null,
                                              groupName: null,
                                            }
                                          : x
                                      )
                                    );
                                    return;
                                  }

                                  setAllGuestParkers((prev) =>
                                    prev.filter((x) => x.draftId !== p.draftId)
                                  );
                                  return;
                                }

                                setRemoveParkerId(p.parkerId);
                                p.groupId
                                  ? setRemoveParkerFromGroup(true)
                                  : setShowRemove(true);
                              },
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div
          className="d-block d-md-none px-3 mt-2"
          style={{ flex: 1, overflowY: "auto" }}
        >
          {groupGuestParkers.length === 0 ? (
            <div className="text-center text-muted py-3">
              No Parkers to Show
            </div>
          ) : (
            groupGuestParkers.map((p) => (
              <div
                key={p.parkerId || p.draftId}
                className="card mb-3 rounded-4 border-1"
                style={getPendingRowStyle(p.isDraft, "white")}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1">
                      <h6 className="mb-1 fw-bold text-primary">{p.name}</h6>

                      {p.groupName ? (
                        groupHasContract(p.groupId) ? (
                          <button
                            type="button"
                            className="badge rounded-pill bg-danger-subtle text-danger border-0 text-decoration-none"
                            style={{ cursor: "pointer" }}
                            onClick={() => openContractModal(p.groupId!)}
                            title="View contract"
                          >
                            {p.groupName}
                          </button>
                        ) : (
                          <span className="badge rounded-pill bg-danger-subtle text-danger">
                            {p.groupName}
                          </span>
                        )
                      ) : (
                        <span className="badge rounded-pill bg-light text-muted">
                          Unassigned
                        </span>
                      )}
                    </div>
                    <ActionDropdown
                      actions={[
                        {
                          label: p.groupId ? "Change Group" : "Assign to Group",
                          icon: p.groupId ? (
                            <Folder2Open
                              size={18}
                              className="text-muted mb-1"
                            />
                          ) : (
                            <FolderPlus size={18} className="text-muted mb-1" />
                          ),
                          onClick: () => {
                            setSelectedParker(p);
                            setShowChangeGroup(true);
                          },
                        },
                        ...(!p.isDraft
                          ? [
                              {
                                label: "Edit Parker",
                                icon: (
                                  <Pencil
                                    size={16}
                                    className="text-muted cursor-pointer mb-1"
                                  />
                                ),
                                onClick: () => {
                                  setSelectedParker(p);
                                  setShowEditParkerModal(true);
                                },
                              },
                            ]
                          : []),
                        {
                          label: p.groupId
                            ? "Remove from Group"
                            : "Remove Parker",
                          icon: (
                            <Trash size={18} className={"text-muted mb-1"} />
                          ),
                          onClick: () => {
                            if (p.isDraft && p.draftId) {
                              if (p.groupId) {
                                setAllGuestParkers((prev) =>
                                  prev.map((x) =>
                                    x.draftId === p.draftId
                                      ? {
                                          ...x,
                                          groupId: null,
                                          groupName: null,
                                        }
                                      : x
                                  )
                                );
                                return;
                              }
                              setAllGuestParkers((prev) =>
                                prev.filter((x) => x.draftId !== p.draftId)
                              );
                              return;
                            }

                            setRemoveParkerId(p.parkerId);
                            p.groupId
                              ? setRemoveParkerFromGroup(true)
                              : setShowRemove(true);
                          },
                        },
                      ]}
                    />
                  </div>

                  <div className="row g-2">
                    <div className="col-6">
                      <div className="bg-light rounded-3 py-2 px-2">
                        <div className="text-muted small">LICENSE PLATES</div>
                        <div className="fw-bold fs-6">
                          {getLpnText(p.vehicles)}
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="bg-light rounded-3 py-2 px-2">
                        <div className="text-muted small">START DATE</div>
                        <div className="fw-bold fs-6">
                          {formatIsoDateShort(p.startDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {showChangeGroup && selectedParker && (
          <ChangeParkerGroupModal
            show={showChangeGroup}
            onClose={() => {
              setShowChangeGroup(false);
              setSelectedParker(null);
            }}
            parkerName={selectedParker.name}
            currentGroupId={selectedParker.groupId}
            groups={groups}
            onConfirm={(groupId) => {
              assignParkerToGroup(selectedParker, groupId);
            }}
          />
        )}
        {showEditParkerModal && selectedParker && (
          <ParkerEditModal
            show={showEditParkerModal}
            onClose={() => {
              setShowEditParkerModal(false);
              setSelectedParker(null);
            }}
            parker={selectedParker}
            setParker={setSelectedParker}
            subStatus={subscription.status}
            subId={subscription.subscriptionId}
            parkers={allGuestParkers}
            setParkers={setAllGuestParkers}
            initialWaitlistRequestPayload={{
              parkerId: selectedParker.parkerId,
              facilityId: subscription.facilityId,
              planEntryId: subscription.plan.planEntryId,
              spacesRequested: 1,
              isAddingToExistingParker: true,
              subscriptionId: subscription.subscriptionId,
            }}
            currentSpaces={1}
            hasWaitlistEntries={false}
            isWaitlistEnabled={false}
            onAddNew={() => setShowAddVehicle(true)}
            onAddExisting={() => {
              openChooseModalForParkersList();
              setShowChooseVehicle(true);
            }}
            onRemoveVehicle={vehicleRemove}
          />
        )}

        <ConfirmModal
          title="Remove Parker From Group"
          message="Are you sure you want to remove this parker from the group?"
          show={removeParkerFromGroup}
          setShow={setRemoveParkerFromGroup}
          onConfirm={() => {
            const parker = allGuestParkers.find(
              (p) => !p.isDraft && p.parkerId === removeParkerId
            );
            if (parker) {
              assignParkerToGroup(parker, null);
            }
          }}
        />

        <AddVehicleModal
          show={showAddVehicle}
          setShow={setShowAddVehicle}
          onSubmit={vehicleAdd}
        />

        <ChooseVehicleModal
          show={showChooseVehicle}
          setShow={setShowChooseVehicle}
          onSubmit={vehicleLink}
          vehicles={chooseModalVehicles}
          exclude={chooseModalExclude}
          onClose={() => setShowChooseVehicle(false)}
        />

        <InviteParkerModal
          show={showAddParker}
          setShow={setShowAddParker}
          isFromGroupContext={true}
          subscriptionStart={subscription.start}
          submit={async (data: InviteParkerForm) => {
            const normalizedGroupId =
              data.subscriptionGroupId === UNASSIGNED_GROUP_ID
                ? null
                : data.subscriptionGroupId;
            parkerInvite(
              data,
              context === Context.FromPlanGroups ? normalizedGroupId! : groupId,
              undefined,
              false
            );
            setShowAddParker(false);
          }}
          groups={context === Context.FromPlanGroups ? groups : undefined}
          isEnhancedGroup={true}
        />

        <ConfirmModal
          title={"Remove Parker"}
          message={
            "Are you sure you want to remove this parker from your plan?"
          }
          show={showRemove}
          setShow={setShowRemove}
          onConfirm={() => {
            if (removeParkerId !== undefined) {
              parkerRemove(removeParkerId!);
            }
          }}
        />

        <ConfirmModal
          title="Discard changes?"
          message="If you cancel now, all unsaved changes will be lost."
          confirmText="Discard"
          show={showCancelConfirm}
          setShow={setShowCancelConfirm}
          onConfirm={cancelChanges}
        />

        <UnsavedChangesModal
          show={showModal}
          onStay={stay}
          onDiscard={discard}
        />
        <CommitChangesConfirmDialog
          show={showCommitConfirmDialog}
          onClose={() => setShowCommitConfirmDialog(false)}
          onConfirm={async () => {
            setShowCommitConfirmDialog(false);
            await saveChanges();
          }}
          isInvoiced={subscription.isAccountsReceivable}
        />
        <GroupModal
          show={showGroupModal}
          onClose={() => setShowGroupModal(false)}
          existingGroups={groups}
          onSubmit={async (name) => {
            await createGroup(name);
          }}
        />
        {contractModalGroup && (
          <ContractDetailsModal
            show={showContractModal}
            contract={contractModalGroup.contractRules ?? []}
            onHide={closeContractModal}
            groupName={contractModalGroup.name ?? ""}
          />
        )}

        <GroupParkersHelpDialog
          show={showHelpDialog}
          onClose={() => setShowHelpDialog(false)}
          groups={groups}
        />
      </div>
    </div>
  );
}
